import { CSSProperties, FC, Fragment, ReactNode } from 'react';
import { Avatar, Col, Row } from 'antd';
import type { AvatarProps } from 'antd';
import { TbCircleCheckFilled } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { MiddleDot } from '@/shared/components/typography';
import WorkspaceResourceAvatar from '@/shared/components/workspace-resources/WorkspaceResourceAvatar';
import { resolveCorporateLogo } from '@/utilities';

export interface DropdownOptionAvatarProps {
    active?: boolean;
    icon?: ReactNode;
    listType?: string;
    props?: Omit<AvatarProps, 'icon' | 'src'>;
    resource?: string;
    src?: AvatarProps['src'];
    style?: CSSProperties;
    website?: string;
}

interface DropdownOptionContentProps {
    avatar?: DropdownOptionAvatarProps;
    avatarStyle?: CSSProperties;
    control?: ReactNode;
    description?: ReactNode;
    detailStyle?: CSSProperties;
    extra?: ReactNode;
    extraAlign?: 'start' | 'center' | 'end';
    icon?: ReactNode;
    metadata?: ReactNode[];
    selected?: boolean;
    singleLine?: boolean;
    title: ReactNode;
    titlePrefix?: ReactNode;
}

const DropdownOptionAvatar: FC<DropdownOptionAvatarProps> = ({
    active,
    icon,
    listType,
    props,
    resource,
    src,
    style,
    website,
}) => {
    const { token } = useAntDesignTheme();

    if (resource) {
        return (
            <WorkspaceResourceAvatar
                active={active}
                listType={listType}
                resource={resource}
                size={typeof props?.size === 'number' ? props.size : 20}
                src={typeof src === 'string' ? src : undefined}
                style={{ display: 'flex', ...props?.style, ...style }}
                website={website}
            />
        );
    }

    const resolvedSrc = website ? resolveCorporateLogo(website, typeof src === 'string' ? src : undefined) : src;

    return (
        <Avatar
            {...props}
            icon={
                icon ? (
                    <span
                        style={{
                            alignItems: 'center',
                            color: token.colorTextTertiary,
                            display: 'flex',
                            height: '100%',
                            justifyContent: 'center',
                            width: '100%',
                        }}
                    >
                        {icon}
                    </span>
                ) : undefined
            }
            shape={props?.shape ?? 'square'}
            size={props?.size ?? 20}
            src={resolvedSrc}
            style={{
                background: token.colorBgContainer,
                border: `1px solid ${token.colorBorder}`,
                display: 'flex',
                ...props?.style,
                ...style,
            }}
        />
    );
};

const DropdownOptionContent: FC<DropdownOptionContentProps> = ({
    avatar,
    avatarStyle,
    control,
    description,
    detailStyle,
    extra,
    extraAlign,
    icon,
    metadata,
    selected = false,
    singleLine = false,
    title,
    titlePrefix,
}) => {
    const { token } = useAntDesignTheme();
    const titleLineHeight = '1.25rem';
    const centeredTitleLine: CSSProperties = {
        alignItems: 'center',
        display: 'flex',
        height: titleLineHeight,
        justifyContent: 'center',
    };
    const avatarIcon = avatar?.icon ?? icon;
    const showAvatar = Boolean(avatar?.resource || avatarIcon || avatar?.src || avatar?.website);
    const detailItems = metadata?.filter((item) => item !== null && item !== undefined && item !== false);

    return (
        <Row align='top' gutter={[8, 0]} wrap={false} style={{ minWidth: 0, textAlign: 'left', width: '100%' }}>
            {control !== undefined && (
                <Col flex='none'>
                    <span style={centeredTitleLine}>{control}</span>
                </Col>
            )}
            {showAvatar ? (
                <Col flex='none'>
                    <DropdownOptionAvatar {...avatar} icon={avatarIcon} style={avatarStyle} />
                </Col>
            ) : null}
            <Col flex='auto' style={{ minWidth: 0 }}>
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: titlePrefix ? '0.5rem' : undefined,
                        lineHeight: titleLineHeight,
                        minHeight: titleLineHeight,
                        minWidth: 0,
                        overflow: singleLine ? 'hidden' : undefined,
                        overflowWrap: singleLine ? 'normal' : 'anywhere',
                        whiteSpace: singleLine ? 'nowrap' : undefined,
                        width: '100%',
                    }}
                >
                    {titlePrefix ? (
                        <span
                            style={{
                                alignItems: 'center',
                                display: 'inline-flex',
                                flex: 'none',
                                justifyContent: 'center',
                                lineHeight: 0,
                            }}
                        >
                            {titlePrefix}
                        </span>
                    ) : null}
                    <div
                        style={{
                            display: 'block',
                            flex: '1 1 auto',
                            minWidth: 0,
                            overflow: singleLine ? 'hidden' : undefined,
                            textOverflow: singleLine ? 'ellipsis' : undefined,
                            whiteSpace: singleLine ? 'nowrap' : undefined,
                        }}
                    >
                        {title}
                    </div>
                </div>
                {detailItems?.length ? (
                    <div
                        style={{
                            alignItems: 'center',
                            color: token.colorTextTertiary,
                            display: 'flex',
                            lineHeight: titleLineHeight,
                            minWidth: 0,
                            overflow: singleLine ? 'hidden' : undefined,
                            whiteSpace: singleLine ? 'nowrap' : undefined,
                            ...detailStyle,
                        }}
                    >
                        {detailItems.map((item, index) => (
                            <Fragment key={index}>
                                {index ? <MiddleDot horizontalMargin='0.375rem' styles={{ flex: 'none' }} /> : null}
                                <small
                                    style={{
                                        minWidth: 0,
                                        overflow: singleLine ? 'hidden' : undefined,
                                        textOverflow: singleLine ? 'ellipsis' : undefined,
                                        whiteSpace: singleLine ? 'nowrap' : undefined,
                                    }}
                                >
                                    {item}
                                </small>
                            </Fragment>
                        ))}
                    </div>
                ) : description !== undefined ? (
                    <div style={{ color: token.colorTextTertiary, lineHeight: titleLineHeight, ...detailStyle }}>
                        <small>{description}</small>
                    </div>
                ) : null}
            </Col>
            {extra !== undefined && (
                <Col flex='none' style={{ alignSelf: extraAlign }}>
                    <span style={centeredTitleLine}>{extra}</span>
                </Col>
            )}
            {selected && (
                <Col flex='none'>
                    <span style={centeredTitleLine}>
                        <TbCircleCheckFilled
                            aria-label='Selected option'
                            role='img'
                            size='1rem'
                            style={{ color: token.colorPrimary }}
                        />
                    </span>
                </Col>
            )}
        </Row>
    );
};

export { DropdownOptionAvatar };
export default DropdownOptionContent;
